import React, {ReactElement, ReactNode} from "react";

import Image, {ImageObject} from "../../image/Image";
import CTAButton, {CTA} from "../../buttons/cta-button/CTAButton";
import ModuleBody from "../../layouts/module-body/ModuleBody";
import TransitionBlock, {
  TRANSITION_BLOCK_FROM,
} from "../../animation/transition-block/TransitionBlock";
import Video, {VideoType} from "../../videos/video/Video";
import Callout, {CalloutProps} from "../../callout/Callout";

import {css} from "/src/utils/utils";

import PlayIconFilled from "/src/svg/play-icon-filled.svg";

import * as styles from "./image-video-text-stacked-single-column.module.scss";

export interface ImageVideoTextStackedSingleColumnProps {
  eyebrowImg?: ImageObject;
  eyebrow?: string;
  heading?: string;
  copy?: string;
  callout?: CalloutProps;
  darkMode?: boolean;
  cta?: CTA;
  image?: ImageObject;
  videoSrc?: string;
  videoThumbnail?: ImageObject;
  mobileImage?: ImageObject;
  featuredComponent?: ReactNode;
  transparentBg?: boolean;
  videoBehavior?: VideoType;
  videoControls?: boolean;
  anchorLinkId?: string;
}

const ImageVideoTextStackedSingleColumn = (
  props: ImageVideoTextStackedSingleColumnProps
): ReactElement => {
  const {
    eyebrowImg,
    eyebrow,
    heading,
    copy,
    cta,
    image,
    mobileImage,
    callout,
    darkMode,
    videoSrc,
    videoThumbnail,
    transparentBg,
    featuredComponent,
    videoBehavior,
    videoControls,
    anchorLinkId,
  } = props;

  return (
    <section
      className={css(
        darkMode ? styles.ivtsSingleColumnDark : "",
        transparentBg ? styles.ivtsSingleColumnTransparent : ""
      )}
      {...(anchorLinkId ? {id: anchorLinkId} : {})}
    >
      <ModuleBody classes={css(styles.ivtsSingleColumn__body)}>
        <div className={styles.ivtsSingleColumn__bodyWrapper}>
          {eyebrowImg && (
            <TransitionBlock
              from={TRANSITION_BLOCK_FROM.bottom}
              delay={0}
              transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
              triggerOnce
            >
              <Image
                image={eyebrowImg}
                classes={styles.ivtsSingleColumn__eyebrowImg}
              />
            </TransitionBlock>
          )}

          {eyebrow && (
            <TransitionBlock
              from={TRANSITION_BLOCK_FROM.bottom}
              delay={25}
              transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
              triggerOnce
            >
              <p className={css("eyebrow", styles.ivtsSingleColumn__eyebrow)}>
                {eyebrow}
              </p>
            </TransitionBlock>
          )}
          <div className={styles.ivtsSingleColumn__contentWrapper}>
            {heading && (
              <div className={styles.ivtsSingleColumn__headingWrapper}>
                <TransitionBlock
                  from={TRANSITION_BLOCK_FROM.bottom}
                  delay={50}
                  transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
                  triggerOnce
                >
                  <h2 className={css("h2", styles.ivtsSingleColumn__heading)}>
                    {heading}
                  </h2>
                </TransitionBlock>
              </div>
            )}

            {copy && (
              <div className={styles.ivtsSingleColumn__copyWrapper}>
                <TransitionBlock
                  from={TRANSITION_BLOCK_FROM.bottom}
                  delay={150}
                  transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
                  triggerOnce
                >
                  <p className={css("p", styles.ivtsSingleColumn__copy)}>
                    {copy}
                  </p>
                </TransitionBlock>
              </div>
            )}
          </div>
          {callout && callout.headline && (
            <TransitionBlock
              from={TRANSITION_BLOCK_FROM.bottom}
              delay={200}
              transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
              triggerOnce
            >
              <Callout
                classes={styles.ivtsSingleColumn__callout}
                darkMode={darkMode === true}
                cta={callout.cta}
                image={callout.image}
                headline={callout.headline}
              />
            </TransitionBlock>
          )}
          {cta && (
            <TransitionBlock
              from={TRANSITION_BLOCK_FROM.bottom}
              delay={250}
              transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
              triggerOnce
            >
              <CTAButton cta={cta} darkMode={darkMode === true} />
            </TransitionBlock>
          )}
        </div>
        {image && !videoSrc && !featuredComponent && (
          <div className={styles.ivtsSingleColumn__imageWrapper}>
            <Image image={image} classes={styles.ivtsSingleColumn__img} />
          </div>
        )}

        {featuredComponent && <>{featuredComponent}</>}

        {(image || mobileImage) && !videoSrc && !featuredComponent && (
          <div className={styles.ivtsSingleColumn__imageWrapperMobile}>
            <Image
              image={(mobileImage || image) as ImageObject}
              classes={styles.ivtsSingleColumn__mobileImg}
            />
          </div>
        )}

        {videoSrc && (
          <div className={styles.ivtsSingleColumn__videoWrapper}>
            <Video
              showControl={
                videoBehavior !== "autoplay" || videoControls === true
              }
              customPlayIcon={PlayIconFilled}
              videoSource={videoSrc}
              type={videoBehavior}
              videoTitle={heading!}
              videoClasses={styles.ivtsSingleColumn__video}
              iconClasses={styles.ivtsSingleColumn__videoIcon}
              videoThumbnail={videoThumbnail}
            />
          </div>
        )}
      </ModuleBody>
    </section>
  );
};

export default ImageVideoTextStackedSingleColumn;
